import { MouseEvent } from 'react';

/**
 * Builds external/internal link element object as per analytics structure by using event object
 * Refer: https://www.npmjs.com/package/react-common-gdlv2
 *
 * @param {Event} event
 * @param {boolean} isExternal
 * @returns element object for analytics
 */
const getLinkElement = (event: MouseEvent<Element, Event>, isExternal: boolean) => ({
  id: event?.currentTarget?.id,
  className: event?.currentTarget?.className,
  value: '',
  type: event?.currentTarget?.getAttribute('type'),
  category: event?.currentTarget?.tagName,
  link: {
    destination: isExternal ? 'External' : 'Internal',
    url: event?.currentTarget?.getAttribute('href')
  }
});

/**
 * Builds external link element object as per analytics structure by using event object
 * Refer: https://www.npmjs.com/package/react-common-gdlv2
 *
 * @param {Event} event
 * @returns element object for analytics
 */
export const getExternalLinkElement = (event: MouseEvent<Element, Event>) =>
  getLinkElement(event, true);

/**
 * Builds internal link element object as per analytics structure by using event object
 * Refer: https://www.npmjs.com/package/react-common-gdlv2
 *
 * @param {Event} event
 * @returns element object for analytics
 */
export const getInternalLinkElement = (event: MouseEvent<Element, Event>) =>
  getLinkElement(event, false);

/**
 * Retrieve search params from session storage and return an
 * instance of URLSearchParams
 */
export const getSearchParamsFromSessionStorage = () => {
  const searchParams = sessionStorage.getItem('searchParams');
  return new URLSearchParams(searchParams ?? '');
};

/**
 * Takes an instance of URLSearchParams and stores params in session storage
 * and sets them into the Adobe Data Layer
 */
export const setSearchParamsOnDataLayer = (
  searchParams: URLSearchParams,
  blacklist = ['token']
) => {
  const params = Array.from(searchParams.entries()).filter(([name]) => !blacklist.includes(name));

  if (!params.length) {
    return;
  }

  sessionStorage.setItem('searchParams', searchParams.toString());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).digitalData.page.query = {
    string: `?${searchParams.toString()}`,
    parameters: params.map(([name, value]) => ({ name, value }))
  };
};
